import cookies from 'browser-cookies';
import {
  AUTHENTICATION_PROVIDER,
  authorizerTokenCookie,
  enableAuthStage,
  forgeRefreshTokenCookie,
} from '@innovyze/stylovyze';

import { store } from '@/store';

const AUTODESK_SIGNIN_HOST = /signin(-stg)?\.autodesk\.com/i;
const nativeLocationAssign = Location.prototype.assign;
const nativeLocationReplace = Location.prototype.replace;

function isIframeEmbedRoute(): boolean {
  return window.location.pathname.startsWith('/embed') && window.self !== window.top;
}

let signInNavigationBlockInstalled = false;

/** Block top-level navigation to Autodesk sign-in inside the Model iframe. */
export function installEmbedSignInNavigationBlock(): void {
  if (signInNavigationBlockInstalled) {
    return;
  }

  signInNavigationBlockInstalled = true;

  const shouldBlock = (url: string | URL): boolean => isAutodeskSignInUrl(String(url));

  Location.prototype.assign = function assign(url: string | URL) {
    if (shouldBlock(url)) {
      console.warn('[bernoulli-embed] blocked location.assign to Autodesk sign-in');
      return;
    }
    return nativeLocationAssign.call(this, url);
  };

  Location.prototype.replace = function replace(url: string | URL) {
    if (shouldBlock(url)) {
      console.warn('[bernoulli-embed] blocked location.replace to Autodesk sign-in');
      return;
    }
    return nativeLocationReplace.call(this, url);
  };
}

/**
 * Prepare Redux/cookie/localStorage state so Stylovyze ApplicationWrapper does not
 * redirect iframe `/embed` loads to `/forge/login` (signin-stg webSdkIframe — blocked in Model).
 *
 * ForgeAuthenticationGate short-circuits to Forge when `loginPicker` is the AuthType.Forge
 * string, so iframe embeds must use boolean `loginPicker: true` plus AUTH_STAGE here.
 * Stale `authentication.provider=forge` in localStorage triggers the same short-circuit.
 */
export function prepareEmbedIframeAuthBypass(): void {
  if (!isIframeEmbedRoute()) {
    return;
  }

  store.dispatch(enableAuthStage());

  // Stale Bernoulli session cookie without Forge refresh → inconsistent state
  // redirect to logoutRoute (/forge/login) → signin-stg iframe.
  if (!cookies.get(forgeRefreshTokenCookie) && cookies.get(authorizerTokenCookie)) {
    cookies.erase(authorizerTokenCookie);
  }

  // Prior standalone Bernoulli visits persist provider=forge; gate treats that like
  // loginPicker: AuthType.Forge and mounts ForgeProvider (signin-stg) regardless of AUTH_STAGE.
  localStorage.removeItem(AUTHENTICATION_PROVIDER);

  installEmbedSignInNavigationBlock();
}

/** @internal test helper */
export function resetEmbedSignInNavigationBlockForTests(): void {
  signInNavigationBlockInstalled = false;
  Location.prototype.assign = nativeLocationAssign;
  Location.prototype.replace = nativeLocationReplace;
}

export function isAutodeskSignInUrl(url: string): boolean {
  return AUTODESK_SIGNIN_HOST.test(url);
}
